// axios API wrapper for prompt toolbox endpoints

import { PROMPT_ENDPOINT } from '@Env/variables';
import { createAxiosInstance } from './axios';
import {
  AhaTicketGeneratorPrompt,
  Comments,
  LibraryPrompt,
  UserPromptVersion,
  UserSavedPrompt,
} from '@Models/types';

export const promptToolboxAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, PROMPT_ENDPOINT);

  return {
    getLibraryPrompts: async (): Promise<LibraryPrompt[]> => {
      const { data } = await axios.post('/api/getLibraryPrompts', {});
      return data;
    },
    createLibraryPrompt: async (
      prompt: LibraryPrompt
    ): Promise<LibraryPrompt> => {
      const { data } = await axios.post('/api/createLibraryPrompt', {
        prompt,
      });
      return data;
    },
    importLibraryPrompt: async (
      prompts: LibraryPrompt[]
    ): Promise<{ message: string; inserted: UserSavedPrompt[] | null }> => {
      const { data } = await axios.post('/api/importLibraryPrompts', {
        prompts,
      });
      return data;
    },
    deleteLibraryPrompt: async (
      libraryId: string
    ): Promise<{ message: string }> => {
      const { data } = await axios.post('/api/deleteLibraryPrompts', {
        libraryId,
      });
      return data;
    },
    commentLibraryPrompt: async (
      promptId: string,
      comment: Comments
    ): Promise<{ message: string }> => {
      const { data } = await axios.post('/api/commentLibraryPrompts', {
        promptId,
        comment,
      });
      return data;
    },
    deleteLibraryPromptComment: async (
      promptId: string,
      commentIndex: number
    ): Promise<{ message: string }> => {
      const { data } = await axios.post('/api/deleteLibraryPromptComment', {
        promptId,
        commentIndex,
      });
      return data;
    },
    voteLibraryPrompt: async (
      promptId: string,
      voteType: string
    ): Promise<{ message: string }> => {
      const { data } = await axios.post('/api/voteLibraryPrompts', {
        promptId,
        voteType,
      });
      return data;
    },
    getUserPrompts: async (): Promise<UserSavedPrompt[]> => {
      const { data } = await axios.post('/api/getUserPrompts', {});
      return data;
    },
    createUserPrompt: async (
      prompt: Omit<UserSavedPrompt, 'id'>
    ): Promise<UserSavedPrompt> => {
      const { data } = await axios.post('/api/createUserPrompt', {
        prompt,
      });
      return data;
    },
    addUserPromptVersion: async (
      promptId: string,
      prompt: Omit<UserPromptVersion, 'timestamp'>,
      version: string
    ): Promise<UserPromptVersion> => {
      const { data } = await axios.post('/api/createUserPromptVersion', {
        promptId,
        prompt,
        version,
      });
      return data;
    },
    deleteUserPrompt: async (
      promptId: string
    ): Promise<{ message: string }> => {
      const { data } = await axios.post('/api/deleteUserPrompt', {
        promptId,
      });
      return data;
    },
    deleteUserPromptVersion: async (
      promptId: string,
      version: string
    ): Promise<{ message: string }> => {
      const { data } = await axios.post('/api/deleteUserPromptVersion', {
        promptId,
        version,
      });
      return data;
    },
    renameUserPrompt: async (
      promptId: string,
      newName: string
    ): Promise<{ message: string }> => {
      const { data } = await axios.post('/api/renameUserPrompt', {
        promptId,
        newName,
      });
      return data;
    },
    getLibraryPrompt: async (libraryId: string): Promise<LibraryPrompt> => {
      const { data } = await axios.get(`/api/getLibraryPrompt`, {
        params: { id: libraryId },
      });
      return data;
    },
    getAhaTicketGeneratorPrompts: async (): Promise<
      AhaTicketGeneratorPrompt[]
    > => {
      const { data } = await axios.get(`/api/getAhaTicketGeneratorPrompts`, {});
      return data;
    },
    deleteAhaTicketGeneratorPrompt: async (promptId: string): Promise<void> => {
      await axios.post('/api/deleteAhaTicketGeneratorPrompt', {
        id: promptId,
      });
    },
    addAhaTicketGeneratorPrompt: async (
      name: string,
      prompt: string,
      author: string
    ): Promise<AhaTicketGeneratorPrompt> => {
      const { data } = await axios.post('/api/addAhaTicketGeneratorPrompt', {
        name,
        prompt,
        author,
      });
      return data.prompt;
    },
    editAhaTicketGeneratorPrompt: async (
      prompt: AhaTicketGeneratorPrompt
    ): Promise<AhaTicketGeneratorPrompt> => {
      const { data } = await axios.post('/api/editAhaTicketGeneratorPrompt', {
        ...prompt,
      });
      return data.prompt;
    },
  };
};
